import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import CustomDialog from '../custom-dialog/custom-dialog';
import PasswordField from '../password-field/password-field';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { useSnackBar } from '../../../contexts/snackbar';
import { IOrderDeliveryTask } from '../../../interfaces/delivery-company/tasks';
import { OrderDeliveryTaskState } from '../../../enums/tasks';
import { get_cod } from '../../../utils/cod';
import { displayMoney } from '../../../utils/money';
import PaidIcon from '@mui/icons-material/Paid';
import { OrderType } from '../../../interfaces/delivery-company/orders';

interface ForceDeliveryActionDialogProps {
  open: boolean;
  onClose: () => void;
  task: IOrderDeliveryTask;
  order: OrderType;
  action: 'complete' | 'fail';
  onSuccess: () => void;
}

const ForceDeliveryActionDialog: React.FC<ForceDeliveryActionDialogProps> = ({
  open,
  onClose,
  task,
  order,
  action,
  onSuccess,
}) => {
  const [understandConsequences, setUnderstandConsequences] = useState(false);
  const [password, setPassword] = useState('');
  const [failureReason, setFailureReason] = useState('');
  const [useCustomReason, setUseCustomReason] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const predefinedReasons = [
    'Shop closed',
    'Wrong amount',
    'Wrong invoice',
    'Person absent',
    'Invoice lost',
  ];

  const handleAction = async () => {
    if (action === 'fail' && !failureReason) {
      setError('Please select or enter a failure reason');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${task.id}/force-${action}/`;
      const payload: any = { password };

      if (action === 'fail') {
        payload.failure_reason = failureReason;
      }

      await axios.post(endpoint, payload, config);

      showMessage(
        `Delivery task ${action === 'complete' ? 'completed' : 'failed'} successfully`,
        'success'
      );
      onSuccess();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setError(
          e.response?.data?.detail ||
            e.response?.data?.password?.[0] ||
            e.response?.data?.failure_reason?.[0] ||
            `Failed to ${action} delivery task`
        );
      } else {
        setError(`Failed to ${action} delivery task`);
      }
    }
    setIsLoading(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      setPassword('');
      setFailureReason('');
      setUseCustomReason(false);
      setUnderstandConsequences(false);
      setError(null);
      onClose();
    }
  };

  return (
    <CustomDialog fullWidth open={open} onClose={handleClose}>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Typography variant="h5" component="h5" sx={{ mb: 1 }}>
          {action === 'complete'
            ? 'Force Complete Delivery'
            : 'Force Fail Delivery'}
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Warning:</strong> You are about to force {action} delivery
          task #{task.id}. This action will bypass normal validation and should
          only be used in exceptional circumstances.
        </Alert>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            mb: 2,
            backgroundColor: 'primary.lighter',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          <PaidIcon color="primary" />
          <Typography variant="body1">
            <strong>COD:</strong> {displayMoney(get_cod(order))}
          </Typography>
        </Box>

        {action === 'complete' &&
          (task.driver_invoice || task.commission_invoice) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This task will be added to the following invoices:
              <ul>
                {task.driver_invoice && (
                  <li>
                    {task.driver_invoice.purpose.toLowerCase()} driver invoice #
                    {task.driver_invoice.id}
                  </li>
                )}
                {task.commission_invoice && (
                  <li>
                    {task.commission_invoice.purpose.toLowerCase()} driver
                    invoice #{task.commission_invoice.id}
                  </li>
                )}
              </ul>
            </Alert>
          )}

        {action === 'fail' && (
          <Box sx={{ mb: 2 }}>
            {!useCustomReason ? (
              <TextField
                select
                fullWidth
                label="Failure Reason"
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select a reason</option>
                {predefinedReasons.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Custom Failure Reason"
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                placeholder="Enter custom failure reason..."
              />
            )}
            <Box sx={{ mt: 1 }}>
              <Checkbox
                checked={useCustomReason}
                onChange={(e) => {
                  setUseCustomReason(e.target.checked);
                  setFailureReason('');
                }}
              />
              Use custom failure reason
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Checkbox
            checked={understandConsequences}
            onChange={(e) => setUnderstandConsequences(e.target.checked)}
          />
          <strong>I understand the consequences of this action</strong>
        </Box>

        <Box sx={{ mb: 2 }}>
          <PasswordField
            label="Password"
            placeholder="Enter your password to confirm"
            value={password}
            onPasswordChange={(value) => setPassword(value)}
            variant="outlined"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}
        >
          <Button onClick={handleClose} disabled={isLoading} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={action === 'complete' ? 'success' : 'error'}
            onClick={handleAction}
            disabled={
              password.length === 0 ||
              !understandConsequences ||
              (action === 'fail' && !failureReason) ||
              isLoading
            }
          >
            {isLoading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </Box>
      </DialogContent>
    </CustomDialog>
  );
};

export default ForceDeliveryActionDialog;
